import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { t, type IntlService } from 'ember-intl'
import type Student from 'teacher-ui-client/models/student'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { differenceWith, find } from 'lodash'
import { htmlSafe, isHTMLSafe } from '@ember/template'
import { Legend, TD, TH, type LegendsItem } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SortColumnIcon } from 'teacher-ui-client/components/sort-column-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/helpers/format-value'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  id: string
  studentId: string
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  averageLexile: Nullable<number>
  booksRead: number
  lexiles: {
    lexile: CategoryKey
    value: number | null
  }[]
}

export type BooksReadByLevelHistoryData = {
  student_id: number
  average_lexile: number
  average_lexile_formatted: string
  '0-300'?: number
  '300-500'?: number
  '500-800'?: number
  '800-2000'?: number
}

interface Args {
  booksReadByLevelData: BooksReadByLevelHistoryData[]
  students: Student[]
  subco: boolean
  uiScope: UIScopeName
  tooltip: string
  studentRouteName: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

type CategoryKey = '0-300' | '300-500' | '500-800' | '800-2000'
const chartCategories: CategoryKey[] = ['0-300', '300-500', '500-800', '800-2000']

function getColor(lexile: CategoryKey) {
  switch (lexile) {
    case '0-300':
      return colors.oceanyBlue300
    case '300-500':
      return colors.grapeyGreen300
    case '500-800':
      return colors.juicyOrange300
    case '800-2000':
      return colors.watermelonyRed300
  }
}

interface ChartArgs {
  data: {
    lexile: CategoryKey
    value: number | null
  } | null
  category: CategoryKey
  showYAxisLabels: boolean
  maxValue: number
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    max: chartOptions.maxValue,
    allowDecimals: false,
    opposite: true,
    tickAmount: 1,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = [
    {
      type: 'bar' as const,
      name: chartOptions.category,
      data: [chartOptions.data?.value ?? 0],
    },
  ]

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}</b>`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [getColor(chartOptions.category)],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class BooksReadByLevelHistory extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.booksReadByLevelData
    return this.args.students.filter(hasId).map((student) => {
      const statRow = find(stats, { student_id: parseInt(student.id, 10) })

      const averageLexile = statRow?.average_lexile ?? null
      const booksRead = chartCategories.reduce((total, lexile) => {
        return total + (statRow?.[lexile] ?? 0)
      }, 0)
      const lexiles = chartCategories.map((lexile) => {
        return { lexile, value: statRow?.[lexile] ?? null }
      })

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        id: student.id,
        studentId: student.id,
        averageLexile,
        booksRead,
        lexiles,
      }
    })
  }

  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.booksReadByLevelData ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get maxBooksRead(): number {
    const booksReadData = this.args.booksReadByLevelData.map((student) => {
      const lexiles = [
        student['0-300'] ?? 0,
        student['300-500'] ?? 0,
        student['500-800'] ?? 0,
        student['800-2000'] ?? 0,
      ]
      return Math.max(...lexiles)
    })
    const highestBooksRead = booksReadData ? Math.max(...booksReadData) : 10
    return Math.ceil(highestBooksRead / 2) * 2 + 2 // extra space on the chart
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      const chartOptions = chartCategories.map((lexile) => {
        const data = row.lexiles.find((lex) => lex.lexile === lexile)

        return buildChartOptions({
          data: data ?? null,
          category: lexile,
          showYAxisLabels: index === 0,
          maxValue: this.maxBooksRead,
        })
      })
      return {
        ...row,
        chartOptions: chartOptions,
      }
    })
  }

  get legendCategories(): LegendsItem[] {
    const { intl } = this
    return [
      {
        color: 'bg-oceany-blue-300',
        label: intl.t('reporting.rex.lexile_ranges.0-300'),
      },
      {
        color: 'bg-grapey-green-300',
        label: intl.t('reporting.rex.lexile_ranges.300-500'),
      },
      {
        color: 'bg-juicy-orange-300',
        label: intl.t('reporting.rex.lexile_ranges.500-800'),
      },
      {
        color: 'bg-watermelony-red-300',
        label: intl.t('reporting.rex.lexile_ranges.800-2000'),
      },
    ]
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  <template>
    <div
      data-test-giraffe-books-read-by-level-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative rounded-sm border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-books-read-by-level-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="flex overflow-x-auto print:block">
        <table data-test-history-table-books-read-by-level class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-oceany-blue-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon class @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}

                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <TH
                class="w-24"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "averageLexile")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.booksRead.averageLexile" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="averageLexile" />
                </div>
              </TH>
              <th class="print-only-table-cell">
                {{t "reporting.charts.booksRead.0-300"}}
              </th>
              <th class="print-only-table-cell">
                {{t "reporting.charts.booksRead.300-500L"}}
              </th>
              <th class="print-only-table-cell">
                {{t "reporting.charts.booksRead.500-800L"}}
              </th>
              <th class="print-only-table-cell">
                {{t "reporting.charts.booksRead.800L+"}}
              </th>
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "booksRead")}}>
                <div class="flex items-center justify-between font-bold">
                  {{t "reporting.charts.booksRead.totalBooksRead"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="booksRead" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH class="border-none bg-white px-0! whitespace-nowrap text-black print:hidden" colspan="4">
                  <Legend
                    data-test-books-read-by-level-legend
                    @title={{t "reporting.chartTitles.booksReadByLevelHistory"}}
                    @categories={{this.legendCategories}}
                    @multipleCharts={{true}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-by-level-student-row
                class="cursor-pointer"
                {{on "click" (fn this.transitionToStudentRoute row.id)}}
              >
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">
                      {{row.teacherName}}
                    </div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD class="text-center">
                  {{formatValue row.averageLexile append="L"}}
                </TD>
                {{#each row.lexiles as |lexile|}}
                  <td class="print-only-table-cell text-center">
                    {{formatValue lexile.value}}
                  </td>
                {{/each}}
                <TD class="text-center">
                  {{formatValue row.booksRead}}
                </TD>
                {{#if this.showChart}}
                  {{#each row.chartOptions as |chartOptions|}}
                    <TD
                      class="{{if @subco 'w-[8%]' 'w-[12%]'}}
                        border-dusty-black-100 cursor-default border-l px-1! py-0! print:hidden"
                    >
                      <BaseHighcharterComponent
                        data-test-books-read-by-level-highchart-row
                        class="h-full"
                        @options={{chartOptions}}
                      />
                    </TD>
                  {{/each}}
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default BooksReadByLevelHistory
